import getGameRewardsNotification from "@casino/actions/Games/getGameRewardsNotification";
import createChangeCurrencyNotification from "@casino/legacy/client/components/Game/createChangeCurrencyNotification";
import useRewardsNotificationStore from "@casino/legacy/client/components/Game/hooks/useNotificationStore";
import { useGamePageData } from "@casino/ui/Games/hooks";
import { useCallback, useEffect, useRef } from "react";

type Args = {
  notification?: RewardsNotification;
  gameId?: string;
};

// Re-asks the server on a currency switch: only it can see the wallet currency and uBTC tag.
const useRewardsNotification = ({ notification, gameId }: Args) => {
  const { inGameCurrency } = useGamePageData();
  const { setNotifications, hasNotification } = useRewardsNotificationStore();
  const isFirstRun = useRef(true);

  const raise = useCallback(
    (next: RewardsNotification) => {
      if (!next.isShowNotification || hasNotification("freeBetReward")) return;

      createChangeCurrencyNotification(
        next.body,
        next.title,
        next.isRequiredToSwitch,
        next.rewardCurrencyIcon,
        next.rewardId,
      );
      setNotifications("freeBetReward");
    },
    [hasNotification, setNotifications],
  );

  useEffect(() => {
    if (notification) raise(notification);
  }, [notification, raise]);

  useEffect(() => {
    if (isFirstRun.current) {
      isFirstRun.current = false;
      return;
    }

    let cancelled = false;
    (async () => {
      const next = await getGameRewardsNotification({
        gameId,
        inGameCurrency: inGameCurrency as Unit,
      });
      if (!cancelled) raise(next);
    })();
    return () => {
      cancelled = true;
    };
  }, [inGameCurrency, gameId, raise]);
};

export default useRewardsNotification;
